import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveCapabilities } from 'store/userGroups';
import {
  selectIntegrationById,
  selectIntegrationsAreFetched
} from 'store/integrations';
import { Booking, IntegrationID } from '__types__';
import { CapabilityEnum } from 'dtos/constants/capability.enum';
import { selectActiveAccountId } from 'store/session';
import { checkOverallAccess } from '../../../../../lib/restrictedAccess/accessCheckers';

/**
 * Custom hook that determines if the current user has access to visitor management
 * for a specific booking. Access is granted if:
 * 1. User has MANAGE_VISITORS capability, OR
 * 2. User is the owner of the booking
 * AND
 * 3. Visitor management integration is enabled
 * 4. At least one vendor is configured
 */
export const useVisitorManagementAccess = (booking: Booking | null) => {
  const currentUserId = useSelector(selectActiveAccountId);
  const activeCapabilities = useSelector(selectActiveCapabilities);
  const integrationsFetched = useSelector(selectIntegrationsAreFetched);
  const visitorManagementIntegration = useSelector((state: any) =>
    selectIntegrationById(state, IntegrationID.VISITOR_MANAGEMENT)
  );

  return useMemo(() => {
    if (!booking || !currentUserId) {
      return false;
    }

    // Check if integrations are loaded
    if (!integrationsFetched) {
      return false;
    }

    // Check if visitor management integration is enabled
    if (!visitorManagementIntegration?.enabled) {
      return false;
    }
    // we cannot check because users without permissions to configure integrations have configuration null
    // // Check if any vendors are configured
    // const hasConfiguredVendors =
    //   visitorManagementIntegration?.configuration?.visitor_management &&
    //   (visitorManagementIntegration.configuration.visitor_management.convergint
    //     ?.enabled ||
    //     visitorManagementIntegration.configuration.visitor_management.splan
    //       ?.enabled);

    // if (!hasConfiguredVendors) {
    //   return false;
    // }

    // Check if user has visitor management capability
    const hasCapability = checkOverallAccess(
      null,
      [],
      [CapabilityEnum.MANAGE_VISITORS, CapabilityEnum.VIEW_VISITORS]
    );

    // Check if user is the owner of the booking
    const isOwner = booking.user_id === currentUserId;

    return hasCapability || isOwner;
  }, [
    booking,
    currentUserId,
    activeCapabilities,
    integrationsFetched,
    visitorManagementIntegration
  ]);
};
